import { CalendarPlanShowcaseConfigGivenOutStatus as LessonStatus } from '@yandex-int/k-common';
import b from 'b_';
import cx from 'classnames';
import { Lock, WarningFilled } from 'education-icons';
import React, { useState, useRef } from 'react';

import { BackgroundImage } from '../../background-image/background-image';
import { Button } from '../../button/button';
import { UI_KIT_TEXT_TYPOGRAPHY } from '../../constants';
import { Tooltip } from '../../LegoTooltip/LegoTooltip';
import Text from '../../text/text';
import { ShowcaseLesson } from '../schowcase-card';

import './showcase-card__lesson-preview.scss';

interface Props {
    className?: string;
    lesson: ShowcaseLesson;
    onClick?: () => void;
}

export const LessonPreview = ({ className, lesson, onClick }: Props) => {
    const { canBeGiven, enabled, id, isLessonBeingGivenNow, name, image, status } = lesson;

    const [isGivenOutTooltipVisible, setGivenOutTooltipVisible] = useState(false);
    const [isUnavailableTooltipVisible, setUnavailableTooltipVisible] = useState(false);

    const unavailableButtonRef = useRef<HTMLDivElement>(null);
    const givenOutRef = useRef<HTMLDivElement>(null);
    const scopeRef = useRef<HTMLDivElement>(null);

    const handleClick = () => {
        if (onClick && canBeGiven) {
            onClick();
        }
    };

    return (
        <div
            className={cx(b('lesson-preview', { 'can-be-given': canBeGiven }), className)}
            onClick={handleClick}
            ref={scopeRef}
        >
            {isLessonBeingGivenNow ? (
                <div className={b('lesson-preview', 'status-message')}>
                    <Text id={'showcaseCard.giveout.now'} />
                </div>
            ) : null}
            {enabled && status === LessonStatus.GIVENOUT && (
                <div
                    className={b('lesson-preview', 'status-badge')}
                    onMouseEnter={() => setGivenOutTooltipVisible(true)}
                    onMouseLeave={() => setGivenOutTooltipVisible(false)}
                    ref={givenOutRef}
                >
                    <WarningFilled className={b('lesson-preview', 'status-badge-icon')} />
                </div>
            )}
            <Tooltip
                anchor={givenOutRef}
                className={b('lesson-preview', 'status-badge-tooltip')}
                direction="top-start"
                hasTail
                key={`lesson-${id}-givenout-tooltip`}
                mainOffset={18}
                scope={scopeRef}
                secondaryOffset={-6}
                tailOffset={8}
                view="default"
                visible={isGivenOutTooltipVisible}
            >
                <Text id={'showcaseCard.giveout.earlier'} />
            </Tooltip>
            <div className={b('lesson-preview', 'image-wrapper')}>
                <BackgroundImage
                    alt="lesson preview image"
                    data-loading="lazy"
                    mix={b('lesson-preview', 'image')}
                    objectFit="cover"
                    src={image}
                />
            </div>
            <div className={b('lesson-preview', 'description')}>
                <Text size={Text.SIZE.XL} weight={Text.WEIGHT.M}>
                    {name}
                </Text>
            </div>
            {onClick && (
                <div className={b('lesson-preview', 'button-with-status')}>
                    {enabled ? (
                        <Button disabled={!canBeGiven} size={Button.SIZE.M}>
                            <Text id={'showcaseCard.giveout.button'} weight={Text.WEIGHT.M} />
                        </Button>
                    ) : (
                        <div className="unavailable-status">
                            <div
                                onMouseEnter={() => setUnavailableTooltipVisible(true)}
                                onMouseLeave={() => setUnavailableTooltipVisible(false)}
                            >
                                <Button
                                    disabled
                                    icon={<Lock className={b('unavailable-status', 'icon')} />}
                                    innerRef={unavailableButtonRef}
                                    transparencyTheme={Button.TRANSPARENCY_THEME.DARK}
                                    view={Button.VIEW.ACTION}
                                />
                                <Tooltip
                                    anchor={unavailableButtonRef}
                                    className={b('unavailable-status', 'tooltip')}
                                    direction="top-start"
                                    hasTail
                                    key={`lesson-${id}-unavailable-tooltip`}
                                    scope={scopeRef}
                                    secondaryOffset={20}
                                    tailOffset={-12}
                                    view="default"
                                    visible={isUnavailableTooltipVisible}
                                >
                                    <Text id="showcaseCard.giveout.unavailableHover" />
                                </Tooltip>
                            </div>
                            <Text
                                id={'showcaseCard.giveout.unavailable'}
                                typography={UI_KIT_TEXT_TYPOGRAPHY.paragraphLRegular}
                            />
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};
